/**
 * API通信クラス
 * サーバーとの通信、認証付きリクエスト、エラーハンドリングを担当
 */
class APIClient {
    constructor(authManager, baseUrl = '/api') {
        this.authManager = authManager;
        this.baseUrl = baseUrl;
    }

    /**
     * 認証付きAPIコール
     */
    async authenticatedFetch(url, options = {}) {
        try {
            const token = await this.authManager.getToken();
            const headers = {
                'Content-Type': 'application/json',
                ...options.headers
            };
            
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(url, {
                ...options,
                headers
            });
            
            // 403エラーの場合、アカウント期限切れまたはサブスクリプション期限切れをチェック
            if (response.status === 403) {
                const data = await response.json();
                if (data.error === 'ACCOUNT_EXPIRED' || data.code === 'TRIAL_EXPIRED' || data.code === 'SUBSCRIPTION_EXPIRED') {
                    
                    // 開発環境ではアカウント期限切れチェックをスキップ
                    if (process.env.REACT_APP_SKIP_ACCOUNT_EXPIRY_CHECK === 'true') {
                        // 成功レスポンスとして扱う
                        return new Response(JSON.stringify({ success: true, data: [] }), {
                            status: 200,
                            statusText: 'OK',
                            headers: response.headers
                        });
                    }
                    
                    // カスタムイベントを発火
                    window.dispatchEvent(new CustomEvent('accountExpired', { 
                        detail: { 
                            message: data.error || data.message || 'Account expired.',
                            code: data.code || 'ACCOUNT_EXPIRED'
                        }
                    }));
                }
                // レスポンスを再構築（json()は一度しか呼べないため）
                return new Response(JSON.stringify(data), {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers
                });
            }
            
            return response;
        } catch (error) {
            console.error('❌ Authenticated fetch error:', error);
            throw error;
        }
    }

    /**
     * エントリ一覧取得
     */
    async getEntries() {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries`);
        return await response.json();
    }

    /**
     * エントリ詳細取得
     */
    async getEntry(entryId) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}`);
        return await response.json();
    }

    /**
     * エントリ作成
     */
    async createEntry(url) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries`, {
            method: 'POST',
            body: JSON.stringify({ url })
        });
        return await response.json();
    }

    /**
     * エントリ削除
     */
    async deleteEntry(entryId) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}`, {
            method: 'DELETE'
        });
        return await response.json();
    }

    /**
     * 商品分析開始
     */
    async startProductAnalysis(entryId) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/products/analyze`, {
            method: 'POST',
            body: JSON.stringify({})
        });
        return await response.json();
    }

    /**
     * 台本生成開始
     */
    async startScriptGeneration(entryId, settings) {
        const body = JSON.stringify({ settings });
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/scripts/generate`, {
            method: 'POST',
            body: body
        });
        return await response.json();
    }

    /**
     * 音声生成開始
     */
    async startAudioGeneration(entryId, revisionNumber = null) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/audios/generate`, {
            method: 'POST',
            body: JSON.stringify({ revisionNumber })
        });
        return await response.json();
    }

    /**
     * クレジット残高取得
     */
    async getCreditBalance() {
        const response = await this.authenticatedFetch(`${this.baseUrl}/credits/balance`);
        return await response.json();
    }

    /**
     * クレジット購入
     */
    async purchaseCredits(amount, plan) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/credits/purchase`, {
            method: 'POST',
            body: JSON.stringify({ amount, plan })
        });
        return await response.json();
    }

    /**
     * 台本更新
     */
    async updateScript(entryId, scriptId, scriptContent) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/scripts/${scriptId}`, {
            method: 'PUT',
            body: JSON.stringify({ scriptContent })
        });
        return await response.json();
    }

    /**
     * 台本取得
     */
    async getScript(entryId, scriptId) {
        const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/scripts/${scriptId}`);
        if (response.status === 404) {
            return { success: false, error: 'Script not found', status: 404 };
        }
        return await response.json();
    }


    /**
     * ユーザー設定API
     */

    // 言語設定を取得
    async getLanguageSetting() {
        try {
            const response = await this.authenticatedFetch(`${this.baseUrl}/user/language`);
            return await response.json();
        } catch (error) {
            console.error('❌ Failed to get language setting:', error);
            throw error;
        }
    }

    // 言語設定を更新
    async updateLanguageSetting(language) {
        try {
            const response = await this.authenticatedFetch(`${this.baseUrl}/user/language`, {
                method: 'PUT',
                body: JSON.stringify({ language })
            });
            return await response.json();
        } catch (error) {
            console.error('❌ Failed to update language setting:', error);
            throw error;
        }
    }

    // プレイヤー設定を取得
    async getPlayerSettings() {
        try {
            const response = await this.authenticatedFetch(`${this.baseUrl}/user/player-settings`);
            return await response.json();
        } catch (error) {
            console.error('❌ Failed to get player settings:', error);
            throw error;
        }
    }

    // プレイヤー設定を更新
    async updatePlayerSettings(settings) {
        try {
            const response = await this.authenticatedFetch(`${this.baseUrl}/user/player-settings`, {
                method: 'PUT',
                body: JSON.stringify(settings)
            });
            return await response.json();
        } catch (error) {
            console.error('❌ Failed to update player settings:', error);
            throw error;
        }
    }

    /**
     * 音声統計情報取得
     */
    async getAudioStats(entryId, audioId) {
        try {
            const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/audios/${audioId}/stats`);
            
            if (!response.ok) {
                return { success: false, data: null };
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('❌ Failed to get audio stats:', error);
            return { success: false, data: null };
        }
    }
    
    /**
     * リビジョン一覧取得
     */
    async getRevisions(entryId) {
        try {
            const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/revisions`);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Revisions API error response:', errorText);
                throw new Error(`Failed to get revisions: ${response.status} - ${errorText}`);
            }
            
            const data = await response.json();
            
            return data.success ? data.data : [];
        } catch (error) {
            console.error('❌ Failed to get revisions:', error);
            throw error;
        }
    }

    /**
     * ライブリビジョンを設定
     */
    async setLiveRevision(entryId, revisionNumber) {
        try {
            const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/revisions/${revisionNumber}/live`, {
                method: 'PUT',
                body: JSON.stringify({})
            });
            if (!response.ok) {
                throw new Error(`Failed to set live revision: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('❌ Failed to set live revision:', error);
            throw error;
        }
    }
}

// Export for use in other modules
export { APIClient };